import {
  IResortTrailmapPage, IResortStrengthsOverview, ISnowfallItem,
  IResortPathInfo,
} from 'api/resort/types';
import classNames from 'classnames';
import { DistanceUnit, SnowUnit } from 'components/blocks/Unit';
import Link from 'next/link';
import { Routes } from 'routes';
import { useOTSTranslation } from 'i18n/useOTSTranslation';

import { ReactComponent as SvgElevation } from 'public/assets/icons/elevation.svg';
import { ReactComponent as SvgArrow } from 'public/assets/icons/rightArrowInCircle.svg';
import { ReactComponent as SvgArrowGreen } from 'public/assets/icons/greenRightArrowInCircle.svg';
import { ReactComponent as SvgTrails } from 'public/assets/icons/trails.svg';
import { ReactComponent as SvgLifts } from 'public/assets/icons/overview-lifts.svg';
import { ReactComponent as SvgSnowfall } from 'public/assets/icons/snowfall.svg';
import { ReactComponent as SvgArrowWhiteBg } from 'public/assets/icons/arrow-white-bg26.svg';
import { useDistanceFormatter } from 'i18n/unitFormat';
import { locale } from 'i18n/constants';
import styles from './styles.module.scss';
import { Category } from '../ResortStrengths/Category';
import { BestTimeSnow } from '../BestTimeSnowUpdated';

interface Props {
  trailmap: IResortTrailmapPage;
  resortStrengths: IResortStrengthsOverview;
  snowfallInfoAnnual: ISnowfallItem[] | null;
  snowfallInfoMonthly: ISnowfallItem[] | null;
  pathInfo: IResortPathInfo;
  annualSnowfall?: number;
  isSummerMode?: boolean;
}

export const ResortGeneralInfo = ({
  trailmap, resortStrengths, annualSnowfall, pathInfo, snowfallInfoMonthly, isSummerMode = false,
}: Props) => {
  const { elevation, runs } = trailmap;
  const { t } = useOTSTranslation('common');

  const totalLifts = Object.values(trailmap.lifts).reduce((prevValue, value) => prevValue + value, 0);
  const liftTypes = Object.values(trailmap.lifts).reduce((prevValue, value) => (value ? prevValue + 1 : prevValue), 0);

  const chartDataForecast = snowfallInfoMonthly ? snowfallInfoMonthly.slice(0, 6) : [];

  const formatAcHa = useDistanceFormatter('acOrHa');

  const skiableTerrainFormatted = formatAcHa(runs.skiableAc);
  const [skiableTerrain] = skiableTerrainFormatted.split(' ');

  return (
    <div className={styles.main}>

      <div className={styles.resort_hero}>
        <div className={styles.resort_info}>
          <div className={styles.column}>
            {!!trailmap && !!elevation && (
              <div className={styles.row}>
                <div className={classNames(styles.column, styles.gray_container)}>
                  <div className={styles.column_header}>
                    <div className={styles.row}>
                      <div className={styles.row_start}>
                        <span><SvgElevation className={styles.svg} width={26} height={19} /></span>
                        {t('resort.overview.elevation')}
                      </div>
                      <div className={styles.row_end}>
                        {isSummerMode ? (
                          <Link href={Routes.resortTripPlanningTrailMap(pathInfo)} className={styles.viewWebsiteLink}>
                            <SvgArrowGreen width={26} height={26} />
                          </Link>
                        ) : (
                          <Link href={Routes.resortTripPlanningTrailMap(pathInfo)} className={styles.viewWebsiteLink}>
                            <SvgArrow width={26} height={26} />
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="column-content">
                    <div className={classNames(styles.row, styles.sm_col)}>
                      <div className="column">
                        <div className={classNames(styles.main_text, { [styles.green]: isSummerMode })}>
                          <DistanceUnit values={elevation.base} type="mOrFeetShort" formatToLocale />
                        </div>
                        <div className={styles.secondary_text}>{t('common.base')}</div>
                      </div>
                      <div className="column">
                        <div className={classNames(styles.main_text, { [styles.green]: isSummerMode })}>
                          <DistanceUnit values={elevation.summit} type="mOrFeetShort" formatToLocale />
                        </div>
                        <div className={styles.secondary_text}>{t('common.summit')}</div>
                      </div>
                      <div className="column">
                        <div className={classNames(styles.main_text, { [styles.green]: isSummerMode })}>
                          <DistanceUnit
                            values={elevation.verticalDrop}
                            type="mOrFeetShort"
                            formatToLocale
                          />
                        </div>
                        <div className={styles.secondary_text}>{t('resort.overview.verticalDrop')}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!isSummerMode && (
              <div className={classNames(styles.row, styles.sm_col)}>
                {!!trailmap?.runs?.skiableAc && !!trailmap?.runs?.total && (
                  <div className={classNames(styles.column, styles.gray_container, styles.second_line_container)}>
                    <div className={styles.column_header}>
                      <div className={styles.row}>
                        <div className={styles.row_start}>
                          <span><SvgTrails className={styles.svg} width={16} height={24} /></span>
                          {t('region.skiReport.trails-abb')}
                        </div>
                        <div className={styles.row_end}>
                          <Link href="#terrain_overview_widget" className={styles.viewWebsiteLink}>
                            <SvgArrow width={26} height={26} />
                          </Link>
                        </div>
                      </div>
                    </div>

                    <div className="column-content">
                      <div className={styles.row}>
                        <div className="column">
                          <div className={styles.main_text}>{runs.total}</div>
                          <div className={styles.secondary_text}>
                            {Number(skiableTerrain).toLocaleString([locale, 'en-US'])}
                            {' '}
                            {t('resort.overview.acresHectars')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {!!trailmap?.lifts && (
                  <div className={classNames(styles.column, styles.gray_container, styles.second_line_container)}>
                    <div className={styles.column_header}>
                      <div className={styles.row}>
                        <div className={styles.row_start}>
                          <span><SvgLifts className={styles.svg} width={26} height={19} /></span>
                          {t('resort.overview.lifts')}
                        </div>
                        <div className={styles.row_end}>
                          <Link href="#total_lifts_widget" className={styles.viewWebsiteLink}>
                            <SvgArrow width={30} height={20} />
                          </Link>
                        </div>
                      </div>

                    </div>
                    <div className="column-content">
                      <div className={styles.row}>
                        <div className="column">
                          <div className={styles.main_text}>
                            {totalLifts}
                          </div>
                          <div className={styles.secondary_text}>
                            {liftTypes}
                            {' '}
                            {t('resort.overview.types')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!!annualSnowfall && !!chartDataForecast && !isSummerMode && (
              <div className={styles.row}>
                <div className={classNames(styles.column, styles.gray_container)}>
                  <div className={styles.column_header}>
                    <div className={styles.row}>
                      <div className={styles.row_start}>
                        <span><SvgSnowfall className={styles.svg} width={24} height={20} /></span>
                        {t('region.skiReport.snowfall')}
                      </div>
                      <div className={styles.row_end}>
                        <Link href={Routes.resortSnowfall(pathInfo)} className={styles.viewWebsiteLink}>
                          <SvgArrow width={26} height={26} />
                        </Link>
                      </div>
                    </div>

                  </div>
                  <div className="column-content">
                    <div className={classNames(styles.row, styles.sm_col)}>
                      <div className={styles.flex1}>
                        <div className={styles.main_text}><SnowUnit values={annualSnowfall} /></div>
                        <div className={styles.secondary_text}>
                          {t('resort.overview.annualSnowfall')}
                        </div>
                      </div>
                      <div className={classNames(styles.column_inner, styles.flex1)}>
                        <BestTimeSnow snowfallInfo={chartDataForecast} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!!resortStrengths.ratings.length && !isSummerMode && (
              <section id={styles.best_known}>
                <div className={styles.container}>
                  <div className={styles.row}>
                    <h4 className={styles.header}>{t('resort.skiReport.bestKnownFor')}</h4>
                    <Link href={Routes.resortTripPlanningReviews(pathInfo)}>
                      <SvgArrowWhiteBg width={26} height={26} />
                    </Link>
                  </div>
                  <div className="content">
                    <ul>
                      {resortStrengths.ratings.map((resort) => (
                        <li key={resort.region.uuid}>
                          <Category
                            key={`${resort.position}-${resort.category}-${resort.region.uuid}`}
                            resortStrengths={resort}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};
